import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PopperProps, TextField } from '@mui/material';
import { Control, Controller, FieldValues, FieldErrors } from 'react-hook-form';
import { formatISO } from 'date-fns';
import { isNull } from 'lodash';

interface DateProps {
  id: string;
  name: string;
  label: string;
  control: Control<FieldValues>;
  readOnly?: boolean;
  popperProps?: Partial<PopperProps>;
  required?: boolean;
  disabled?: boolean;
  notClearable?: boolean;
  onChange?: (date: Date | null) => void;
  errors?: FieldErrors | null;
}

export const DateField = function ({
  id,
  name,
  label,
  control,
  readOnly = false,
  popperProps,
  required = false,
  disabled = false,
  notClearable = false,
  onChange,
  errors = null,
}: DateProps) {
  //TODO: We need to re-evaluate this component.  It looks like it has some issues when user manually types values.
  // We should take a look at this when we have more time to test as this will every component that uses a datePicker.
  const handleChange = (
    date: Date | null,
    callback: (...event: unknown[]) => void
  ) => {
    try {
      // Inform parent of raw Date or null
      onChange && onChange(date);

      // Update react-hook-form value: ISO string when date present, otherwise null
      if (isNull(date)) {
        return callback(null);
      }

      return callback(formatISO(date));
    } catch {
      onChange && onChange(null);
      return callback(null);
    }
  };

  return (
    <Controller
      rules={{ required }}
      control={control}
      name={name}
      render={({ field: { onChange, value }, fieldState }) => {
        const getByPath = (obj: unknown, path: string) => {
          if (!obj || !path) return undefined;
          const parts = path.split('.');
          let cur: unknown = obj;
          for (const p of parts) {
            if (cur == null) return undefined;
            if (typeof cur === 'object') {
              cur = (cur as Record<string, unknown>)[p];
            } else {
              return undefined;
            }
          }
          return cur;
        };

        const errorMessageFromErrors = () => {
          if (!errors) return undefined;
          // errors may be a FieldErrors object; check for direct message
          if (typeof (errors as any)?.message === 'string')
            return String((errors as any).message);
          const full = getByPath(errors, name);
          if (full && typeof (full as any).message === 'string')
            return String((full as any).message);
          const last = name.split('.').pop() || '';
          if (typeof errors === 'object' && errors !== null) {
            const lastObj = (errors as Record<string, unknown>)[last];
            if (lastObj && typeof (lastObj as any).message === 'string')
              return String((lastObj as any).message);
          }
          return undefined;
        };

        const errorMessage = fieldState.error
          ? String(fieldState.error.message)
          : errorMessageFromErrors();
        return (
          <DatePicker
            componentsProps={{
              actionBar: {
                actions: notClearable ? [] : ['clear'],
              },
            }}
            PopperProps={popperProps}
            readOnly={readOnly}
            onChange={(date) => handleChange(date, onChange)}
            renderInput={(props) => (
              <TextField
                {...props}
                id={id}
                variant="standard"
                error={!!errorMessage}
                helperText={errorMessage}
              />
            )}
            label={label}
            value={value}
            disabled={disabled}
          />
        );
      }}
    />
  );
};
